import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  CircularProgress,
} from '@mui/material';
import { ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import 'react-confirm-alert/src/react-confirm-alert.css';
import { ConfirmDialogProps, CustomDialogProps } from './types/DialogTypes';
import { 
  StyledDialogContent, 
  StyledDialogActions, 
  DestructiveButton, 
  CancelButton 
} from './styles/DialogStyles';
import * as ReactDOM from 'react-dom/client';
import { theme } from '../../theme';

const CustomDialog: React.FC<CustomDialogProps> = ({
  action,
  actionName,
  title,
  body,
  cancelText = 'Atšaukti',
  isDestructive = false,
  onCancel,
  isOpen,
  onClose,
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleAction = async () => {
    try {
      setIsLoading(true);
      await action();
      onClose();
    } catch (error) {
      console.error('Action failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    onCancel?.();
    onClose();
  };

  return (
    <Dialog 
      open={isOpen} 
      onClose={handleCancel}
      aria-labelledby="confirm-dialog-title"
    >
      <DialogTitle id="confirm-dialog-title">{title}</DialogTitle>
      <StyledDialogContent>
        {body}
      </StyledDialogContent>
      <StyledDialogActions>
        <CancelButton
          onClick={handleCancel}
          variant="contained"
          disabled={isLoading}>
          {cancelText}
        </CancelButton>
        {isDestructive ? (
          <DestructiveButton
            onClick={handleAction}
            variant="contained"
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={20} /> : undefined}
          >
            {actionName}
          </DestructiveButton>
        ) : (
          <Button
            onClick={handleAction}
            color="primary"
            variant="contained"
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={20} /> : undefined}
          >
            {actionName}
          </Button>
        )}
      </StyledDialogActions>
    </Dialog>
  );
};

export const confirmDialog = ({
  action,
  actionName,
  title,
  body,
  cancelText,
  isDestructive,
  onCancel,
}: ConfirmDialogProps): void => {
  const mountNode = document.createElement('div');
  document.body.appendChild(mountNode);
  
  const root = ReactDOM.createRoot(mountNode);
  
  const onClose = () => {
    root.unmount();
    document.body.removeChild(mountNode);
  };
  
  root.render(
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <CustomDialog
          action={action}
          actionName={actionName}
          title={title}
          body={body}
          cancelText={cancelText}
          isDestructive={isDestructive}
          onCancel={onCancel}
          isOpen={true}
          onClose={onClose}
        />
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

export default confirmDialog; 